import { useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, Copy, Check, Eye, LogOut } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function SecurityDialog({ open, onOpenChange, forced = false }) {
  const { user, checkAuth } = useAuth();
  const enabled = !!user?.mfa_enabled;
  // Iter 308 — while an admin is viewing as this user the prompt is the
  // target's, not theirs: offer a session-only bypass instead of enrolment.
  const masq = user?.masqueraded_by || null;
  const [stage, setStage] = useState("idle"); // idle | setup | backup | disable
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setStage("idle");
    setSetupData(null);
    setCode("");
    setBackupCodes([]);
  };

  const close = (v) => {
    if (forced && !enabled) return; // cannot dismiss until enrolled
    if (!v) reset();
    onOpenChange(v);
  };

  const skipForMasquerade = async () => {
    setBusy(true);
    try {
      await api.post("/auth/masquerade/skip-mfa-prompt");
      await checkAuth();
      reset();
      onOpenChange(false);
      toast.success(`Continuing as ${user?.name} without two-factor setup (this View as session only)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const exitMasquerade = async () => {
    setBusy(true);
    try { await api.post("/auth/masquerade/stop"); } catch { /* ignore */ }
    window.location.href = "/dashboard";
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
      setStage("setup");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code });
      setBackupCodes(data.backup_codes || []);
      setStage("backup");
      setCode("");
      await checkAuth();
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      await checkAuth();
      toast.success("Two-factor authentication disabled");
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="security-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            {masq
              ? `You're viewing as ${user?.name}. Two-factor settings belong to them and can't be changed from a View as session.`
              : forced && !enabled
                ? "Your organization requires two-factor authentication. Set it up to continue."
                : "Add an extra layer of security using an authenticator app."}
          </DialogDescription>
        </DialogHeader>

        {/* MASQUERADE — session-only bypass instead of enrolment */}
        {masq && stage === "idle" && (
          <div className="space-y-4" data-testid="mfa-masquerade-panel">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <span className="text-sm font-medium text-slate-700">{user?.name}'s status</span>
              <span data-testid="mfa-status" className={`text-sm font-semibold ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
                {enabled ? "Enabled" : "Not enabled"}
              </span>
            </div>
            {forced && !enabled && (
              <div className="flex gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <Eye className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{user?.name} hasn't set up two-factor authentication yet. You can continue without it for this View as session — the bypass is recorded in the activity log and {user?.name} will still be asked at their next sign-in.</span>
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button data-testid="mfa-masquerade-exit" variant="outline" onClick={exitMasquerade} disabled={busy} className="flex-1">
                <LogOut className="h-4 w-4" /> Exit View as
              </Button>
              <Button data-testid="mfa-masquerade-skip" onClick={forced && !enabled ? skipForMasquerade : () => close(false)} disabled={busy}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                {forced && !enabled ? "Continue without 2FA" : "Close"}
              </Button>
            </div>
          </div>
        )}

        {/* IDLE */}
        {!masq && stage === "idle" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <span className="text-sm font-medium text-slate-700">Status</span>
              <span data-testid="mfa-status" className={`text-sm font-semibold ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
                {enabled ? "Enabled" : "Not enabled"}
              </span>
            </div>
            {!enabled ? (
              <Button data-testid="mfa-enable-start" onClick={startSetup} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enable two-factor authentication
              </Button>
            ) : (
              <Button data-testid="mfa-disable-start" variant="outline" onClick={() => setStage("disable")} className="w-full border-red-200 text-red-600 hover:bg-red-50">
                Disable two-factor authentication
              </Button>
            )}
          </div>
        )}

        {/* SETUP */}
        {stage === "setup" && setupData && (
          <div className="space-y-4">
            <ol className="list-decimal space-y-1 pl-4 text-sm text-slate-600">
              <li>Scan the QR code with Google Authenticator, Authy, or 1Password.</li>
              <li>Enter the 6-digit code to confirm.</li>
            </ol>
            <div className="flex justify-center">
              <img src={setupData.qr_code} alt="MFA QR code" className="h-44 w-44 rounded-lg border border-slate-200" data-testid="mfa-qr" />
            </div>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
              Manual key: <span className="font-mono font-medium text-slate-700">{setupData.secret}</span>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mfa-code">Verification code</Label>
              <Input id="mfa-code" data-testid="mfa-enable-code" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="text-center tracking-[0.4em]" />
            </div>
            <Button data-testid="mfa-enable-confirm" onClick={confirmEnable} disabled={busy || code.length < 6} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & enable
            </Button>
          </div>
        )}

        {/* BACKUP CODES */}
        {stage === "backup" && (
          <div className="space-y-4">
            <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Save these backup codes somewhere safe. Each can be used once if you lose your device.
            </div>
            <div data-testid="mfa-backup-codes" className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 p-3 font-mono text-sm">
              {backupCodes.map((c) => <div key={c} className="text-center text-slate-700">{c}</div>)}
            </div>
            <Button variant="outline" onClick={copyBackup} className="w-full">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy codes"}
            </Button>
            <Button data-testid="mfa-backup-done" onClick={() => close(false)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">Done</Button>
          </div>
        )}

        {/* DISABLE */}
        {stage === "disable" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Enter a current authenticator code or a backup code to disable two-factor authentication.</p>
            <Input data-testid="mfa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="text-center tracking-widest" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStage("idle"); setCode(""); }} className="flex-1">Cancel</Button>
              <Button data-testid="mfa-disable-confirm" onClick={confirmDisable} disabled={busy || !code} className="flex-1 bg-red-600 text-white hover:bg-red-700">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Disable
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
